import { useQuery } from "@tanstack/react-query";

import type { SharedSession } from "./types";
import {
  buildSharedSessionQueryParams,
  buildSharedSessionsQueryKey,
  hasSharedSessionTarget,
} from "./shared-session-cache-utils";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";

interface UseSharedSessionsOptions {
  workspaceId: number | undefined;
  promptId: number | undefined;
  workflowId?: number;
  skillCollectionId?: string;
  enabled?: boolean;
}

export const useSharedSessions = ({
  workspaceId,
  promptId,
  workflowId,
  skillCollectionId,
  enabled = true,
}: UseSharedSessionsOptions) => {
  const authContext = useAuth();

  return useQuery<SharedSession[]>(
    buildSharedSessionsQueryKey({
      workspaceId,
      promptId,
      workflowId,
      skillCollectionId,
    }),
    async () => {
      const params = buildSharedSessionQueryParams({
        promptId,
        workflowId,
        skillCollectionId,
      });
      const response = await fetch(
        `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/shared_sessions?${params}`,
        {
          headers: { Authorization: `Bearer ${authContext?.userToken}` },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch shared sessions");
      }

      const data = await response.json();
      return (data.shared_sessions ?? []) as SharedSession[];
    },
    {
      // Run whenever we have the required ids — do NOT gate on hasExistingSessions.
      // Gating on it causes the query to become disabled after the last session is
      // deleted (exists → false) while its stale cached data persists, making old
      // sessions reappear the next time a new link is created.
      enabled:
        enabled &&
        !!workspaceId &&
        hasSharedSessionTarget({
          promptId,
          workflowId,
          skillCollectionId,
        }),
      // Keep the previous list visible while a background refetch is in flight so
      // the manage panel never flashes empty.
      keepPreviousData: true,
    },
  );
};
